import { motion } from "framer-motion";
import { Award, Download, History, RotateCcw } from "lucide-react";
import { achievementStates, earnedCount, studySnapshot, analectById, quoteLabel } from "../../engine";
import { UserPanel } from "../user/UserPanel";
import { useGame } from "../../state/GameProvider";

/**
 * 我 — growth made visible: rank, achievements, and the learning record.
 *
 * The record is deliberately the passages themselves rather than a session
 * log: "what have I actually read" is the question a student wants answered.
 */
export function MeView() {
  const { state, actions } = useGame();
  const snap = studySnapshot(state.progress);
  const achievements = achievementStates(state.progress);
  const earned = earnedCount(state.progress);

  const recent = Object.entries(state.progress.records)
    .map(([id, record]) => ({ id: Number(id), at: record.masteredAt ?? 0 }))
    .filter((entry) => entry.at > 0)
    .sort((a, b) => b.at - a.at)
    .slice(0, 12);

  return (
    <div className="view me">
      <header className="view-head">
        <h2>我的修業</h2>
        <p>
          {snap.rank.rank.label} · 已通 {snap.mastered}/{snap.total} 章 · 成就 {earned.done}/{earned.total}
        </p>
      </header>

      <UserPanel />

      <section className="panel">
        <div className="panel-title">
          <Award size={15} />
          成就
        </div>
        <div className="ach-grid">
          {achievements.map((entry, index) => (
            <motion.div
              key={entry.achievement.id}
              className={`ach ${entry.achievement.tier}${entry.done ? " done" : ""}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.02 }}
            >
              <div className="ach-head">
                <strong>{entry.achievement.label}</strong>
                {!entry.done && (
                  <small>
                    {entry.have}/{entry.need}
                  </small>
                )}
              </div>
              <p>{entry.done ? entry.achievement.earned : entry.achievement.requirement}</p>
              {!entry.done && (
                <i className="ach-bar">
                  <i style={{ width: `${entry.percent}%` }} />
                </i>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-title">
          <History size={15} />
          最近讀通
        </div>
        {recent.length === 0 ? (
          <p className="empty">還沒有通關的章。先破一則試試。</p>
        ) : (
          <ul className="record-list">
            {recent.map((entry) => {
              const analect = analectById.get(entry.id);
              if (!analect) return null;
              return (
                <li key={entry.id}>
                  <button onClick={() => actions.start({ id: entry.id })}>
                    <b>{quoteLabel(analect)}</b>
                    <span>{analect.text.replace(/[⑴-⒇]/g, "").slice(0, 26)}…</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className="me-actions">
        <button onClick={actions.exportSave}>
          <Download size={15} /> 匯出進度
        </button>
        <button
          className="danger"
          onClick={() => {
            if (window.confirm("確定要清空全部修業紀錄嗎？此動作無法復原。")) actions.reset();
          }}
        >
          <RotateCcw size={15} /> 重置
        </button>
      </div>
    </div>
  );
}
